import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, MessageCircle } from "lucide-react";
import { waLink } from "@/data/site";

const lines = ["We distribute", "what industry &", "homes rely on."];

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.15 * i, ease: [0.22, 1, 0.36, 1] },
  }),
};

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative min-h-screen overflow-hidden pt-[72px]"
      data-testid="hero-section"
    >
      {/* Accent glows */}
      <motion.div style={{ y }} className="pointer-events-none absolute inset-0">
        <div className="absolute -top-20 left-[8%] h-[420px] w-[420px] rounded-full bg-[#38bdf8]/20 blur-[140px]" />
        <div className="absolute top-1/3 right-[6%] h-[380px] w-[380px] rounded-full bg-[#facc15]/12 blur-[150px]" />
        <div className="absolute bottom-0 left-1/2 h-[360px] w-[360px] rounded-full bg-[#e879f9]/12 blur-[150px]" />
      </motion.div>

      <div className="relative z-10 mx-auto max-w-[1400px] px-6 lg:px-10 pt-16 lg:pt-24">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.8 }}
          className="overline mb-8 flex items-center gap-3"
          data-testid="hero-overline"
        >
          <span className="inline-block h-px w-10 bg-white/40" />
          Authorised Dealers & Distributors
        </motion.p>

        <h1 className="font-display text-white leading-[0.92] tracking-tighter text-5xl sm:text-7xl lg:text-[7.5rem]">
          {lines.map((line, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span
                custom={i}
                variants={lineVariants}
                initial="hidden"
                animate="visible"
                className="block font-light"
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.9 }}
          className="mt-10 max-w-xl text-lg text-white/60 leading-relaxed"
          data-testid="hero-subtitle"
        >
          One trusted trading house for three essentials — <span className="text-[#38bdf8]">DOWSIL</span> silicone sealants,{" "}
          <span className="text-[#facc15]">Chennai Express</span> urad dhal, and{" "}
          <span className="text-[#e879f9]">Wopper</span> laundry chemicals — supplied across Tamil Nadu.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.9 }}
          className="mt-12 flex flex-wrap items-center gap-4"
        >
          <button
            onClick={() => scrollTo("products")}
            className="group inline-flex items-center gap-2 rounded-full bg-white px-7 py-4 text-sm font-medium text-black transition-transform duration-300 hover:-translate-y-1"
            data-testid="hero-explore-btn"
          >
            Explore Products
            <ArrowDownRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
          </button>
          <a
            href={waLink()}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2 rounded-full border border-white/15 px-7 py-4 text-sm font-medium text-white transition-colors duration-300 hover:border-white/40"
            data-testid="hero-whatsapp-btn"
          >
            <MessageCircle className="h-4 w-4" />
            Chat on WhatsApp
          </a>
        </motion.div>

        <motion.div
          style={{ opacity }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.1, duration: 1 }}
          className="mt-20 grid grid-cols-2 gap-8 border-t border-white/10 pt-8 sm:grid-cols-4"
          data-testid="hero-stats"
        >
          {[
            ["3", "Trusted brands"],
            ["1000+", "Orders fulfilled"],
            ["B2B & Retail", "Supply channels"],
            ["Coimbatore", "Based in TN"],
          ].map(([k, v]) => (
            <div key={v}>
              <div className="font-display text-3xl lg:text-4xl font-medium tracking-tight">
                {k}
              </div>
              <div className="mt-1 text-sm text-white/50">{v}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
